import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";
import FeatureGates from "@atlassian/feature-gate-js-client";

import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src/data-event-props";

import { Banner, BannerProps } from "../../../components/global-nav/banner/src";
import {
    ListOfLinks,
    ListOfLinksHeaderText,
    ListOfLinksProps
} from "../../../components/global-nav/list-of-links/src";
import Link from "../../../components/wpl/link/src";

// ==================================================================
// Type Props
// ==================================================================
export interface ResourcesDropdownProps {
    productKey: string;
    className?: string;
}

// ==================================================================
// Styles
// ==================================================================
const listOfLinksRowStyle = css({
    marginBlockStart: token("space.300"),
    display: "grid",
    [media.above.sm]: {
        gridTemplateColumns: "1fr 1fr 1fr"
    },
    [media.below.md]: {
        gridTemplateColumns: "1fr"
    }
});

const listOfLinksStyle = css({
    marginBlockEnd: token("space.200")
});

const bannerStyle = css({
    paddingInlineEnd: token("space.250"),
    paddingBlockEnd: token("space.400")
});

const atlassianTeam23Style = css({
    "textDecoration": "none",
    "color": "#091e42",
    "&:hover": {
        ".atlassianteam23": {
            color: "#0065ff"
        }
    },
    "&:visited": {
        color: "#091e42"
    }
});

// ==================================================================
// Rendering Functions
// ==================================================================
export const UnleashPromo = () => {
    const intl = useIntl();

    const descriptionText = intl.formatMessage({
        id: "resources-dropdown.sidebar.unleash.at-description",
        defaultMessage:
            "Product updates, hands-on training, and technical demos - catch all that and more at our biggest agile & DevOps event."
    });

    const CTAText = intl.formatMessage({
        id: "resources-dropdown.sidebar.unleash.watch-now.cta",
        defaultMessage: "Watch now"
    });

    return (
        <div>
            <a
                href="https://events.atlassian.com/unleash?ref=Unleash&utm_source=marketing&utm_campaign=acquisition&utm_content=global_subnav&utm_medium=web"
                css={atlassianTeam23Style}
                data-event="clicked"
                data-event-component="linkButton"
                data-event-container="image"
            >
                <img
                    css={css({
                        width: "180px"
                    })}
                    id="ed54ffcf"
                    alt=""
                    className="component__image"
                    src="https://wac-cdn.atlassian.com/dam/jcr:175bb96f-63a8-4b66-bd94-a3e5ea7e7d1d/Social%20Unleash%20193x122.png?cdnVersion=1234"
                />
            </a>
            <div
                css={css({
                    marginBlockStart: token("space.150"),
                    marginBlockEnd: token("space.600")
                })}
            >
                <div
                    css={css({
                        display: "block"
                    })}
                >
                    <a
                        href="https://events.atlassian.com/unleash?ref=Unleash&utm_source=marketing&utm_campaign=acquisition&utm_content=global_subnav&utm_medium=web"
                        className="atlassianteam23"
                        css={css({
                            fontSize: token("space.200"),
                            marginBlockEnd: token("space.100"),
                            marginBlockStart: token("space.200")
                        })}
                        data-event="clicked"
                        data-event-component="linkButton"
                        data-event-container="globalNavWacDropdownContainer"
                    >
                        {"Atlassian Presents: Unleash"}
                        <div
                            css={css({
                                color: "#505f79",
                                fontSize: token("space.150"),
                                marginBlockEnd: token("space.075")
                            })}
                        >
                            <p
                                css={css({
                                    maxWidth: "270px"
                                })}
                            >
                                {descriptionText}
                            </p>
                        </div>
                        <Link
                            css={css({
                                "&:hover, &:focus": {
                                    textShadow: "none"
                                }
                            })}
                            text={CTAText}
                            href="https://events.atlassian.com/unleash?ref=Unleash&utm_source=marketing&utm_campaign=acquisition&utm_content=global_subnav&utm_medium=web"
                            type="basic"
                            customLinkColor="#0052cc"
                        />
                    </a>
                </div>
            </div>
        </div>
    );
};

export const AtlassianTeam23Promo = () => {
    const intl = useIntl();

    const descriptionText = intl.formatMessage({
        id: "resources-dropdown.sidebar.atlassian23.at-description",
        defaultMessage:
            "Catch up on the latest Atlassian product announcements and innovations shared live on stage in Las Vegas."
    });

    const watchNowText = intl.formatMessage({
        id: "resources-dropdown.sidebar.atlassian23.watch-now",
        defaultMessage: "Watch now"
    });

    return (
        <div>
            <img
                id="ed54ffcf"
                alt=""
                className="component__image"
                src="https://wac-cdn.atlassian.com/dam/jcr:41056f05-6198-44af-8680-1efc2e926789/Screen%20Shot%202023-01-05%20at%203.48%201.jpg?cdnVersion=1223"
            />
            <div
                css={css({
                    marginBlockStart: token("space.150"),
                    marginBlockEnd: token("space.600")
                })}
            >
                <a
                    href="https://events.atlassian.com/team23/v/s-1430980?ref=Team_23&utm_source=butterbar&utm_campaign=od_drop2&utm_medium=internal&utm_type=Team_23_404"
                    css={atlassianTeam23Style}
                >
                    <div
                        css={css({
                            display: "block"
                        })}
                    >
                        <div
                            className="atlassianteam23"
                            css={css({
                                fontSize: token("space.200"),
                                marginBlockEnd: token("space.100"),
                                marginBlockStart: token("space.200")
                            })}
                        >
                            {"Atlassian Team '23"}
                        </div>
                        <div
                            css={css({
                                color: "#505f79",
                                fontSize: token("space.150"),
                                marginBlockEnd: token("space.075")
                            })}
                        >
                            {descriptionText}
                        </div>
                        <Link
                            text={watchNowText}
                            href="https://events.atlassian.com/team23/v/s-1430980?ref=Team_23&utm_source=butterbar&utm_campaign=od_drop2&utm_medium=internal&utm_type=Team_23_404"
                            type="basic"
                            customLinkColor="#0052cc"
                        />
                    </div>
                </a>
            </div>
        </div>
    );
};

export const ResourcesMain = ({
    className,
    productKey
}: ResourcesDropdownProps) => {
    const intl = useIntl();

    const gate: boolean = FeatureGates.checkGate("gwp-1311-server-eol-gate");

    const learnListProps: ListOfLinksProps = {
        isSideBar: false,
        headerText: intl.formatMessage({
            id: "resources-dropdown.main.learn",
            defaultMessage: "Learn"
        }),
        listOfLinkWithProps: [
            {
                href: "https://university.atlassian.com/",
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.university",
                    defaultMessage: "Atlassian University"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/team-playbook"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.tp",
                    defaultMessage: "Atlassian Playbook"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: "https://support.atlassian.com/",
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.startup",
                    defaultMessage: "Product Documentation"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: "https://developer.atlassian.com/",
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.dr",
                    defaultMessage: "Developer Resources"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            }
        ]
    };

    const supportListProps: ListOfLinksProps = {
        isSideBar: false,
        headerText: intl.formatMessage({
            id: "resources-dropdown.main.support",
            defaultMessage: "Support"
        }),
        listOfLinkWithProps: [
            {
                href: useLocalizedHref("/community"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.ac",
                    defaultMessage: "Atlassian Community"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: "https://support.atlassian.com/",
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.as",
                    defaultMessage: "Atlassian Support"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/migration"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.migration",
                    defaultMessage: "Atlassian Migration Program"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/enterprise/services"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.es",
                    defaultMessage: "Enterprise Services"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/partners"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.partners",
                    defaultMessage: "Partner Support"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref(
                    "/licensing/purchase-licensing#pricing-discounts"
                ),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.p&l",
                    defaultMessage: "Purchasing & Licensing"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            }
        ]
    };

    const connectListProps: ListOfLinksProps = {
        isSideBar: false,
        headerText: intl.formatMessage({
            id: "resources-dropdown.main.connect",
            defaultMessage: "Connect"
        }),
        listOfLinkWithProps: [
            {
                href: useLocalizedHref("/company"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.company",
                    defaultMessage: "About us"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/company/careers"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.careers",
                    defaultMessage: "Careers"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/blog"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.blog",
                    defaultMessage: "Work Life Blog"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/company/events"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "resources-dropdown.main.events",
                    defaultMessage: "Events"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            }
        ]
    };

    const SupportForServerProps: BannerProps = {
        title: intl.formatMessage({
            id: "resources-dropdown.server-support-msg.title",
            defaultMessage: "Support for Server products ends February 15, 2024"
        }),
        body: intl.formatMessage({
            id: "resources-dropdown.server-support-msg.description",
            defaultMessage:
                "With end of support for our Server products fast approaching, create a winning plan for your Cloud migration with the Atlassian Migration Program."
        }),
        linkText: intl.formatMessage({
            id: "resources-dropdown.server-support-msg.button",
            defaultMessage: "Assess my options"
        }),
        linkUrl: useLocalizedHref("/migration/assess/journey-to-cloud"),
        dataEventProps: {
            "data-product-key": productKey as ProductKeyTypes,
            "data-event-component": "linkButton",
            "data-event-container": "globalNavWacDropdownContainer",
            "data-event": "clicked"
        }
    };

    return (
        <div
            className={className}
            css={css({
                width: "100%"
            })}
        >
            <div css={listOfLinksRowStyle}>
                <div css={listOfLinksStyle}>
                    <ListOfLinks {...learnListProps} />
                </div>
                <div css={listOfLinksStyle}>
                    <ListOfLinks {...supportListProps} />
                </div>
                <div css={listOfLinksStyle}>
                    <ListOfLinks {...connectListProps} />
                </div>
            </div>
            {gate ? (
                <div css={bannerStyle}>
                    <Banner {...SupportForServerProps} />
                </div>
            ) : null}
        </div>
    );
};

export const ResourcesSideBar = ({ className }: ResourcesDropdownProps) => {
    const intl = useIntl();
    const sideBarTitle = intl.formatMessage({
        id: "resources-dropdown.sidebar.title",
        defaultMessage: "What's new"
    });

    return (
        <div className={className}>
            <ListOfLinksHeaderText
                isSideBar={false}
                headerText={sideBarTitle}
            />
            <UnleashPromo />
        </div>
    );
};
